import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    IconButton,
    Button,
    CircularProgress,
    Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import ExternalTransactionService from '../../Services/externalTransaction.service';
import NoDocumentsPdf from '../../assests/pdf/NoDocument.pdf';

function PaperComponent(props) {
    const nodeRef = useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper ref={nodeRef} {...props} />
        </Draggable>
    );
}

const EditExternalTransactionDialog = ({
    editBankTransactionDialog,
    handleCloseBankEditTransactionDialog,
    editedTransaction,
    changeNewBankTransaction,
     handleEditBankTransaction,
    handleDelete,
    selectedTBank,
    token,
    viewOnly = false
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const fileInputRef = useRef(null);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [resolvedDocumentUrl, setResolvedDocumentUrl] = useState(null);
    const [showDocument, setShowDocument] = useState(false);

    useEffect(() => {
        if (!editedTransaction.transactionDate) {
            changeNewBankTransaction('transactionDate', dayjs());
        }
    }, [editBankTransactionDialog, editedTransaction]);

    // Always show the document automatically whenever one exists (and in view-only mode). The eye
    // button can still toggle it off afterwards.
    useEffect(() => {
        if (editBankTransactionDialog) {
            setShowDocument(viewOnly || !!editedTransaction?.documentUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editBankTransactionDialog, viewOnly, editedTransaction?.documentUrl]);

    const handleClose = () => {
        handleCloseBankEditTransactionDialog();
    }

    const documentCaseId = selectedTBank?.caseId || editedTransaction?.caseId;

    // Resolve a viewable (http) URL for the inline preview. Presigned URLs are used directly;
    // a stored S3 object key is exchanged for a fresh presigned URL.
    useEffect(() => {
        let active = true;
        const resolve = async () => {
            const url = editedTransaction?.documentUrl;
            if (!url) {
                setResolvedDocumentUrl(null);
                return;
            }
            if (/^https?:\/\//i.test(url)) {
                setResolvedDocumentUrl(url);
                return;
            }
            if (!editedTransaction?.id || !documentCaseId) {
                setResolvedDocumentUrl(null);
                return;
            }
            try {
                const response = await ExternalTransactionService.GetDocumentUrlById(
                    editedTransaction.id,
                    documentCaseId,
                    token
                );
                if (response.ok) {
                    const result = await response.json();
                    if (active) setResolvedDocumentUrl(result.url || null);
                }
            } catch (error) {
                if (active) setResolvedDocumentUrl(null);
            }
        };
        if (editBankTransactionDialog) resolve();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editBankTransactionDialog, editedTransaction?.documentUrl, editedTransaction?.id]);

    const handleViewDocument = async () => {
        if (!editedTransaction?.documentUrl) return;
        // Prefer the already-resolved viewable URL; otherwise fall back to the raw value or resolve it.
        if (resolvedDocumentUrl && /^https?:\/\//i.test(resolvedDocumentUrl)) {
            window.open(resolvedDocumentUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        // If we already hold an http(s) URL (e.g. presigned), open it directly; otherwise resolve a
        // fresh pre-signed URL from the stored object key.
        if (/^https?:\/\//i.test(editedTransaction.documentUrl)) {
            window.open(editedTransaction.documentUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (!editedTransaction?.id || !documentCaseId) return;
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.GetDocumentUrlById(
                editedTransaction.id,
                documentCaseId,
                token
            );
            if (response.ok) {
                const result = await response.json();
                if (result.url) {
                    window.open(result.url, '_blank', 'noopener,noreferrer');
                }
            }
        } catch (error) {
            console.error('Error opening document:', error);
        } finally {
            setDocumentLoading(false);
        }
    };

    const handleUploadDocumentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDocumentSelected = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        if (!editedTransaction?.id || !documentCaseId) {
            toast.error(t('FinsiteFetchFailed'));
            return;
        }
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.UploadDocument(
                editedTransaction.id,
                documentCaseId,
                file,
                token
            );
            if (response.ok) {
                const result = await response.json();
                changeNewBankTransaction('documentUrl', result.presignedUrl);
                toast.success(t('documentUploadedSuccess'));
            } else {
                const errorText = await response.text();
                toast.error(`${t('ErrorUploadingFiles')}: ${errorText}`);
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error(t('ErrorUploadingFiles'));
        } finally {
            setDocumentLoading(false);
        }
    };

    const handleDeleteDocument = async () => {
        if (!editedTransaction?.id || !documentCaseId) return;
        setDocumentLoading(true);
        try {
            const response = await ExternalTransactionService.DeleteDocumentUrl(
                editedTransaction.id,
                documentCaseId,
                token
            );
            if (response.ok) {
                changeNewBankTransaction('documentUrl', null);
                toast.success(t('DocumentUrlDeletedSuccessfully'));
            } else {
                const errorText = await response.text();
                toast.error(`${t('ErrorDeletingDocumentUrl')}: ${errorText}`);
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error(t('ErrorDeletingDocumentUrl'));
        } finally {
            setDocumentLoading(false);
        }
    };

    const isFormValid = !!(
        editedTransaction?.description &&
        editedTransaction.reference1 &&
        editedTransaction.transactionDate &&
        editedTransaction.amount
    );

    const hasDocument = !!editedTransaction?.documentUrl || viewOnly;
    const showDocumentPane = hasDocument && showDocument;
    const iframeSrc = resolvedDocumentUrl
        ? `${resolvedDocumentUrl}#page=1&view=FitH`
        : NoDocumentsPdf;

    const inputSx = {
        height: '44px',
        padding: '0 10px',
        borderRadius: '8px',
        background: '#F5F5F6',
        border: '1px solid transparent',
        '&:focus-within': {
            border: '2px solid #304FFF',
        },
    };

    // Keep the button icon on the correct side and spaced properly in RTL.
    const btnIconSx = direction === 'rtl'
        ? {
            textTransform: 'none',
            '& .MuiButton-startIcon': { marginRight: '-2px', marginLeft: '8px' },
        }
        : { textTransform: 'none' };

    return (
        <Dialog
            open={editBankTransactionDialog}
            onClose={handleCloseBankEditTransactionDialog}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth
            maxWidth={showDocumentPane ? 'lg' : 'sm'}
            PaperProps={{
                sx: {
                    borderRadius: '14px',
                    direction,
                },
            }}
        >
            <div style={{
                position: 'absolute',
                top: '10px',
                [direction === 'rtl' ? 'right' : 'left']: '10px',
                zIndex: 10, // Ensures it stays on top
            }}>
                <Tooltip title={t("Cancel")}>
                    <IconButton onClick={handleClose} aria-label="cancel" size="small">
                        <CancelIcon style={{ color: '#304FFF' }} />
                    </IconButton>
                </Tooltip>
            </div>

            <DialogTitle
                id="draggable-dialog-title"
                sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.15rem', pb: 1.5, cursor: 'move' }}
            >
                {viewOnly ? t("ViewBankCommand") : t("EditBankCommand")}
            </DialogTitle>

            <DialogContent className={`${direction}`} sx={{ px: 3, py: 1, overflowX: 'hidden' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: showDocumentPane ? 'row' : 'column',
                        gap: showDocumentPane ? '24px' : '0px',
                        alignItems: showDocumentPane ? 'stretch' : 'stretch',
                        flexWrap: showDocumentPane ? 'wrap' : 'nowrap',
                    }}
                >
                    {/* ───────── Form column (2-column grid) ───────── */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: showDocumentPane
                                ? '1fr'
                                : 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '14px 16px',
                            alignContent: 'start',
                            flex: showDocumentPane ? '1 1 360px' : '1 1 auto',
                            minWidth: '280px',
                            padding: showDocumentPane ? '18px' : '4px 2px',
                            borderRadius: '12px',
                            background: showDocumentPane ? '#FAFBFF' : 'transparent',
                            border: showDocumentPane ? '1px solid #ECEEF6' : 'none',
                            alignSelf: 'flex-start',
                        }}
                    >
                        {/* Description */}
                        <div className="form-row-item full-width">
                            <div className="form-row-item-title">
                                {t('Description')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                disabled={viewOnly}
                                placeholder={t('Description')}
                                value={editedTransaction?.description || ''}
                                onChange={(e) => changeNewBankTransaction('description', e.target.value)}
                                className="add-client-input-style full-width"
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle full-width ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: inputSx,
                                }}
                                inputProps={{
                                    style: {
                                        width: '100%',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                                required
                            />
                        </div>

                        {/* Reference */}
                        <div className="form-row-item full-width">
                            <div className="form-row-item-title">
                                {t('Reference')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                disabled={viewOnly}
                                placeholder={t('Reference')}
                                value={editedTransaction?.reference1 || editedTransaction?.reference || ''}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    changeNewBankTransaction('reference1', next);
                                    changeNewBankTransaction('reference', next);
                                }}
                                className="add-client-input-style full-width"
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle full-width ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: inputSx,
                                }}
                                inputProps={{
                                    style: {
                                        width: '100%',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                                required
                            />
                        </div>

                        {/* Transaction Date */}
                        <div className="form-row-item full-width">
                            <div className="form-row-item-title">
                                {t('TransactionDate')}<span className="required-asterisk">*</span>
                            </div>
                            <CustomDatePicker
                                date={
                                    editedTransaction.transactionDate
                                        ? editedTransaction.transactionDate
                                        : null
                                }
                                onDateChange={(date) => {
                                    if (date && !viewOnly) {
                                        changeNewBankTransaction('transactionDate', date);
                                    }
                                }}
                                height="44px"
                            />
                        </div>

                        {/* Amount */}
                        <div className="form-row-item full-width">
                            <div className="form-row-item-title">
                                {(selectedTBank?.foreignCurrencyCode != null && selectedTBank?.foreignCurrencyCode !== undefined)
                                    ? t('CurrencyAmount')
                                    : t('Amount')}
                                <span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                disabled={viewOnly}
                                placeholder={(selectedTBank?.foreignCurrencyCode != null && selectedTBank?.foreignCurrencyCode !== undefined)
                                    ? t('CurrencyAmount')
                                    : t('Amount')}
                                value={editedTransaction.amount || ''}
                                onChange={(e) => changeNewBankTransaction('amount', e.target.value)}
                                className="add-client-input-style full-width"
                                variant="standard"
                                type="number"
                                InputProps={{
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle full-width ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: inputSx,
                                }}
                                inputProps={{
                                    style: {
                                        width: '100%',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                                required
                            />
                        </div>

                        {/* Document actions (edit mode only) */}
                        {!viewOnly && (
                            <div className="form-row-item full-width" style={{ gridColumn: '1 / -1' }}>
                                <div className="form-row-item-title">
                                    {t('Document')}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept="application/pdf,image/*"
                                    onChange={handleDocumentSelected}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    {editedTransaction?.documentUrl && (
                                        <Button
                                            variant="outlined"
                                            startIcon={showDocumentPane ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            onClick={() => setShowDocument((prev) => !prev)}
                                            disabled={documentLoading}
                                            sx={{ ...btnIconSx, flex: '1 1 auto', borderColor: '#304FFF', color: '#304FFF' }}
                                            dir="ltr"
                                        >
                                            {showDocumentPane ? t('HideDocument') : t('ViewDocument')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        startIcon={documentLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                                        onClick={handleUploadDocumentClick}
                                        disabled={documentLoading}
                                        sx={{ ...btnIconSx, flex: '1 1 auto', backgroundColor: '#304FFF', boxShadow: 'none', '&:hover': { backgroundColor: '#1E3ACC', boxShadow: 'none' } }}
                                        dir="ltr"
                                    >
                                        {editedTransaction?.documentUrl ? t('ReplaceDocument') : t('UploadDocument')}
                                    </Button>
                                    {editedTransaction?.documentUrl && (
                                        <Tooltip title={t('Delete')}>
                                            <span>
                                                <IconButton onClick={handleDeleteDocument} disabled={documentLoading} aria-label="delete-document">
                                                    <DeleteIcon style={{ color: 'red' }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ───────── Document preview column ───────── */}
                    {showDocumentPane && (
                        <div
                            style={{
                                flex: '2 1 480px',
                                minWidth: '320px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div className="form-row-item-title" style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{t('Document')}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {editedTransaction?.documentUrl && (
                                        <Tooltip title={t('OpenInNewTab')}>
                                            <IconButton size="small" onClick={handleViewDocument} disabled={documentLoading} sx={{ color: '#304FFF' }}>
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!viewOnly && (
                                        <Tooltip title={t('HideDocument')}>
                                            <IconButton size="small" onClick={() => setShowDocument(false)} sx={{ color: '#666' }}>
                                                <VisibilityOffIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            <iframe
                                key={resolvedDocumentUrl || 'no-document'}
                                title="external-transaction-document"
                                src={iframeSrc}
                                style={{
                                    width: '100%',
                                    height: '70vh',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                }}
                            />
                        </div>
                    )}
                </div>
            </DialogContent>

            {/* ===== Action buttons (hidden in view-only mode) ===== */}
            {!viewOnly && (
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '12px',
                        px: 3,
                        pb: 2.5,
                        pt: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={handleDelete}
                        sx={{
                            ...btnIconSx,
                            borderColor: '#E53935',
                            color: '#E53935',
                            borderRadius: '8px',
                            px: 2.5,
                            '&:hover': { borderColor: '#C62828', backgroundColor: 'rgba(229,57,53,0.08)' },
                        }}
                        dir="ltr"
                    >
                        {t('Delete')}
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleEditBankTransaction}
                        disabled={!isFormValid}
                        sx={{
                            ...btnIconSx,
                            backgroundColor: '#304FFF',
                            borderRadius: '8px',
                            px: 3,
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#1E3ACC', boxShadow: 'none' },
                            '&:disabled': { backgroundColor: '#C5CAE9', color: '#fff' },
                        }}
                        dir="ltr"
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default EditExternalTransactionDialog;
